import React, { useState, useEffect } from "react";
import {
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useGetModulesQuery,
} from "../../../store/auth/authApi";

const toModuleId = (value) => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export default function SubscriptionModal({
  isOpen,
  onClose,
  subscription = null,
  onSuccess,
}) {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    member_signup_price: "",
    sms_units: "",
    email_count: "",
    support_service: "BASIC",
    modules: [],
  });
  const [apiError, setApiError] = useState("");

  const [createSubscription, { isLoading: isCreating }] =
    useCreateSubscriptionMutation();
  const [updateSubscription, { isLoading: isUpdating }] =
    useUpdateSubscriptionMutation();
  const { data: modulesData, isLoading: modulesLoading } = useGetModulesQuery({
    all: 1,
    per_page: 200,
  });

  const isEdit = !!subscription;
  const isLoading = isCreating || isUpdating;

  // Helper function to format number with commas
  const formatNumberWithCommas = (value) => {
    if (!value) return "";
    const num = parseFloat(value);
    if (isNaN(num)) return "";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    if (subscription) {
      setFormData({
        name: subscription.name || "",
        price: formatNumberWithCommas(subscription.price) || "",
        member_signup_price:
          formatNumberWithCommas(subscription.memberSignupPrice) || "",
        sms_units: subscription.smsUnits || "",
        email_count: subscription.emailCount || "",
        support_service: subscription.supportService || "BASIC",
        modules: (subscription.modules || [])
          .map((m) => toModuleId(m?.id))
          .filter(Boolean),
      });
    } else {
      setFormData({
        name: "",
        price: "",
        member_signup_price: "",
        sms_units: "",
        email_count: "",
        support_service: "BASIC",
        modules: [],
      });
    }
    setApiError("");
  }, [subscription]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (apiError) {
      setApiError("");
    }

    // Format and validate based on field type
    let formattedValue = value;

    if (name === "price" || name === "member_signup_price") {
      // Remove all non-numeric characters except decimal point
      let cleanValue = value.replace(/[^0-9.]/g, "");

      // Ensure only one decimal point
      const parts = cleanValue.split(".");
      if (parts.length > 2) {
        cleanValue = parts[0] + "." + parts.slice(1).join("");
      }

      // Limit to 2 decimal places
      if (parts[1] && parts[1].length > 2) {
        cleanValue = parts[0] + "." + parts[1].substring(0, 2);
      }

      // Add comma separators for thousands
      const [integerPart, decimalPart] = cleanValue.split(".");
      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ","
      );
      formattedValue = decimalPart
        ? `${formattedInteger}.${decimalPart}`
        : formattedInteger;
    } else if (name === "sms_units" || name === "email_count") {
      // Only allow whole numbers for SMS units and email count
      formattedValue = value.replace(/[^0-9]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleModuleToggle = (moduleId) => {
    const id = toModuleId(moduleId);
    if (!id) return;

    setFormData((prev) => ({
      ...prev,
      modules: prev.modules.includes(id)
        ? prev.modules.filter((existingId) => existingId !== id)
        : [...prev.modules, id],
    }));
  };

  const isModuleSelected = (moduleId) =>
    formData.modules.includes(toModuleId(moduleId));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    // Format the data before submission - remove commas from price fields
    const formattedData = {
      ...formData,
      price: parseFloat(String(formData.price).replace(/,/g, "")) || 0,
      member_signup_price:
        parseFloat(String(formData.member_signup_price).replace(/,/g, "")) || 0,
      sms_units: parseInt(formData.sms_units, 10) || 0,
      email_count: parseInt(formData.email_count, 10) || 0,
      modules: formData.modules.map(toModuleId).filter(Boolean),
    };

    try {
      if (isEdit) {
        await updateSubscription({
          id: subscription.id,
          ...formattedData,
        }).unwrap();
      } else {
        await createSubscription(formattedData).unwrap();
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving subscription:", error);
      const errorMessage =
        error?.data?.message ||
        error?.data?.error ||
        error?.error ||
        error?.message ||
        "Unable to save subscription. Please try again.";
      setApiError(errorMessage);
    }
  };

  if (!isOpen) return null;

  const moduleList = Array.isArray(modulesData?.data) ? modulesData.data : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {isEdit ? "Edit Subscription" : "Create New Subscription"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            type="button"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {apiError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm font-medium text-red-800">{apiError}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subscription Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price (₦)
              </label>
              <input
                type="text"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A]"
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Member Signup Price (₦)
              </label>
              <input
                type="text"
                name="member_signup_price"
                value={formData.member_signup_price}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A]"
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMS Units
              </label>
              <input
                type="text"
                name="sms_units"
                value={formData.sms_units}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A]"
                placeholder="0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Count
              </label>
              <input
                type="text"
                name="email_count"
                value={formData.email_count}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A]"
                placeholder="0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Support Service
              </label>
              <select
                name="support_service"
                value={formData.support_service}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A]"
                required
              >
                <option value="BASIC">Basic</option>
                <option value="STANDARD">Standard</option>
                <option value="PREMIUM">Premium</option>
              </select>
            </div>
          </div>

          {/* Modules Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Select Modules
            </label>

            {modulesLoading ? (
              <div className="flex justify-center py-8">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading modules...</span>
                </div>
              </div>
            ) : moduleList.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">
                No modules available. Add modules from the Modules page first.
              </p>
            ) : (
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4">
                <div className="space-y-4">
                  {moduleList.map((module) => (
                    <div
                      key={module.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`module-${module.id}`}
                          checked={isModuleSelected(module.id)}
                          onChange={() => handleModuleToggle(module.id)}
                          className="h-4 w-4 text-[#8B313A] focus:ring-[#8B313A] border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`module-${module.id}`}
                          className="ml-3 text-sm font-medium text-gray-700 cursor-pointer"
                        >
                          {module.name}
                        </label>
                      </div>

                      {/* Sub-modules */}
                      {module.subModules && module.subModules.length > 0 && (
                        <div className="ml-7 mt-2 space-y-2">
                          {module.subModules.map((subModule) => (
                            <div
                              key={subModule.id}
                              className="flex items-center"
                            >
                              <input
                                type="checkbox"
                                id={`submodule-${subModule.id}`}
                                checked={isModuleSelected(subModule.id)}
                                onChange={() =>
                                  handleModuleToggle(subModule.id)
                                }
                                className="h-4 w-4 text-[#8B313A] focus:ring-[#8B313A] border-gray-300 rounded"
                              />
                              <label
                                htmlFor={`submodule-${subModule.id}`}
                                className="ml-3 text-sm text-gray-600 cursor-pointer"
                              >
                                {subModule.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-sm text-gray-500 mt-2">
              Selected modules: {formData.modules.length}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-[#8B313A] text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {isLoading
                ? "Saving..."
                : isEdit
                ? "Update Subscription"
                : "Create Subscription"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
